import { memo, useEffect, useRef } from "react"
import { EditorContent } from "@tiptap/react"
import { useFormContext, useWatch } from "react-hook-form"

import {
	htmlToMarkdown,
	markdownToHtml,
} from "@productlane/api/src/lib/v2/markdown"
import { cn } from "@productlane/lib/src/cn"
import { typographyVariants } from "@productlane/ui"

import { useIssueDescriptionEditor } from "../editor/use-issue-description-editor"

interface Props {
	name: string
	placeholder: string
	readOnly: boolean
}

interface Seed {
	markdown: string
	roundTripped: string
}

// An edit that lands back on the seed's round-tripped form is not a real change,
// so the untouched original goes back into the form and never reaches Linear.
export function resolveEmittedMarkdown(
	markdown: string,
	seed: Seed | null,
): string {
	if (!seed) return markdown
	return markdown === seed.roundTripped ? seed.markdown : markdown
}

// Tiptap emits an update for work the user did not do: the autolink pass after
// a seed, and any re-seed from Zero. Writing those to the form would mark it
// dirty, which blocks the reset that carries newer server content in, and lets
// a browser holding a stale replica push that stale copy to Linear.
export function isUserEdit({
	hasInteracted,
	nextMarkdown,
	currentMarkdown,
}: {
	hasInteracted: boolean
	nextMarkdown: string
	currentMarkdown: string | undefined
}): boolean {
	if (!hasInteracted) return false
	return nextMarkdown !== (currentMarkdown ?? "")
}

export const MarkdownDescriptionField = memo(
	({ name, placeholder, readOnly }: Props) => {
		const { control, getValues, setValue } = useFormContext()
		const value = (useWatch({ control, name }) as string | undefined) ?? ""
		const seedRef = useRef<Seed | null>(null)
		const emittedRef = useRef<string | null>(null)
		const hasInteractedRef = useRef(false)
		const seedingRef = useRef(false)

		const editor = useIssueDescriptionEditor({
			placeholder,
			editable: !readOnly,
			className: cn(
				typographyVariants({ variant: "editor" }),
				"w-full text-[16px] leading-[1.55] tracking-tight",
				"focus:outline-none focus:ring-0",
			),
			onUpdate: (html, isEmpty) => {
				const next = resolveEmittedMarkdown(
					isEmpty ? "" : htmlToMarkdown(html),
					seedRef.current,
				)
				const isEdit = isUserEdit({
					hasInteracted: hasInteractedRef.current && !seedingRef.current,
					nextMarkdown: next,
					currentMarkdown: getValues(name) as string | undefined,
				})
				if (!isEdit) return
				emittedRef.current = next
				setValue(name, next, { shouldDirty: true })
			},
		})

		useEffect(() => {
			if (!editor || editor.isDestroyed) return
			if (value === emittedRef.current) return
			if (value === seedRef.current?.markdown) return

			let cancelled = false
			let settle: ReturnType<typeof setTimeout> | undefined
			void markdownToHtml(value).then((html) => {
				if (cancelled || editor.isDestroyed) return
				seedingRef.current = true
				editor.commands.setContent(html)
				emittedRef.current = null
				// The baseline is read once the autolink pass has run, so linkifying
				// a bare URL reads as the seed rather than as an edit.
				settle = setTimeout(() => {
					seedingRef.current = false
					if (cancelled || editor.isDestroyed) return
					seedRef.current = {
						markdown: value,
						roundTripped: htmlToMarkdown(editor.getHTML()),
					}
				}, 0)
			})
			return () => {
				cancelled = true
				clearTimeout(settle)
				seedingRef.current = false
			}
		}, [editor, value])

		useEffect(() => {
			if (!editor || editor.isDestroyed) return
			editor.setEditable(!readOnly)
		}, [editor, readOnly])

		// A drop lands content without the editor ever taking focus, so it counts
		// as an interaction too.
		useEffect(() => {
			if (!editor || editor.isDestroyed) return
			const onInteract = () => {
				hasInteractedRef.current = true
			}
			editor.on("focus", onInteract)
			editor.view.dom.addEventListener("drop", onInteract)
			return () => {
				editor.off("focus", onInteract)
				editor.view.dom.removeEventListener("drop", onInteract)
			}
		}, [editor])

		return <EditorContent editor={editor} className="mt-4 w-full" />
	},
)
